import { useEffect, useRef, type ReactNode } from 'react';
import { createPortal } from 'react-dom';

/**
 * Drawer de navegacao do mobile (RNF16).
 *
 * Um painel que cobre a tela e um dialogo modal, e dialogo modal tem obrigacoes
 * que costumam ser esquecidas:
 *  - fecha no Escape e no clique fora;
 *  - prende o foco enquanto esta aberto, senao o Tab passeia pela pagina que
 *    esta atras do vidro e o leitor de tela le conteudo invisivel;
 *  - devolve o foco para quem abriu ao fechar;
 *  - trava a rolagem do fundo.
 *
 * Tudo isso a mao, sem biblioteca: e pouca coisa e nao justifica dependencia.
 */

interface MobileDrawerProps {
  readonly open: boolean;
  /** Estavel entre renders (useCallback no chamador): entra na lista do efeito. */
  readonly onClose: () => void;
  /** Id do painel, referenciado pelo aria-controls do botao que abre. */
  readonly id: string;
  /** Nome do dialogo para leitor de tela. */
  readonly label: string;
  readonly children: ReactNode;
}

const FOCUSABLE_SELECTOR = [
  'a[href]',
  'area[href]',
  'button:not([disabled])',
  'input:not([disabled])',
  'select:not([disabled])',
  'textarea:not([disabled])',
  '[tabindex]:not([tabindex="-1"])',
].join(',');

export function MobileDrawer({ open, onClose, id, label, children }: MobileDrawerProps): JSX.Element | null {
  const panelRef = useRef<HTMLDivElement | null>(null);

  useEffect(() => {
    if (!open) {
      return undefined;
    }
    const panel = panelRef.current;
    if (panel === null) {
      return undefined;
    }

    // Guarda quem tinha o foco antes de abrir. Sem isso, ao fechar o usuario de
    // teclado volta para o inicio do documento e perde o lugar.
    const opener = document.activeElement instanceof HTMLElement ? document.activeElement : null;

    // Recalcula a cada uso porque o conteudo do menu pode mudar com a rota.
    // Elementos sem retangulo (escondidos) nao contam como parada de Tab.
    const focusables = (): readonly HTMLElement[] =>
      Array.from(panel.querySelectorAll<HTMLElement>(FOCUSABLE_SELECTOR)).filter(
        (element) => element.getClientRects().length > 0,
      );

    const firstOnOpen = focusables()[0];
    (firstOnOpen ?? panel).focus();

    const handleKeyDown = (event: KeyboardEvent): void => {
      if (event.key === 'Escape') {
        event.preventDefault();
        onClose();
        return;
      }
      if (event.key !== 'Tab') {
        return;
      }

      const items = focusables();
      const first = items[0];
      const last = items[items.length - 1];

      if (first === undefined || last === undefined) {
        // Painel sem nada focavel: o Tab nao pode vazar para o fundo.
        event.preventDefault();
        panel.focus();
        return;
      }

      const active = document.activeElement;
      if (!(active instanceof HTMLElement) || !panel.contains(active)) {
        event.preventDefault();
        first.focus();
        return;
      }

      if (event.shiftKey && active === first) {
        event.preventDefault();
        last.focus();
      } else if (!event.shiftKey && active === last) {
        event.preventDefault();
        first.focus();
      }
    };

    // Fase de captura: intercepta o Escape antes que qualquer campo do menu o
    // consuma (um combobox aberto, por exemplo).
    document.addEventListener('keydown', handleKeyDown, true);

    const previousOverflow = document.body.style.overflow;
    document.body.style.overflow = 'hidden';

    return () => {
      document.removeEventListener('keydown', handleKeyDown, true);
      document.body.style.overflow = previousOverflow;
      // `isConnected` evita tentar focar um elemento que saiu da arvore.
      if (opener !== null && opener.isConnected) {
        opener.focus();
      }
    };
  }, [open, onClose]);

  if (!open) {
    return null;
  }

  // Portal no body: o drawer fica imune a `overflow` e `transform` de qualquer
  // ancestral, que sao as duas causas classicas de painel fixo cortado.
  return createPortal(
    <div className="lg:hidden">
      <div
        aria-hidden="true"
        onClick={onClose}
        className="fixed inset-0 z-backdrop animate-fade-in bg-scrim/60"
      />
      <div
        ref={panelRef}
        id={id}
        role="dialog"
        aria-modal="true"
        aria-label={label}
        tabIndex={-1}
        className="fixed inset-y-0 left-0 z-drawer flex w-drawer animate-slide-in-left flex-col border-r border-line bg-surface shadow-2xl outline-none"
      >
        {/*
          O botao de fechar nao e enfeite: no celular nao ha tecla Escape, e
          tocar fora e um gesto que nem todo mundo descobre. Ele tambem garante
          que sempre exista ao menos um alvo focavel para a armadilha de foco.
          Posicionado por cima para nao empurrar o menu para baixo.
        */}
        <button
          type="button"
          onClick={onClose}
          aria-label="Fechar menu de navegacao"
          className="absolute right-2 top-3 rounded-lg p-2 text-ink-muted transition-colors hover:bg-ink/5 hover:text-ink"
        >
          <svg
            viewBox="0 0 24 24"
            className="h-5 w-5"
            fill="none"
            stroke="currentColor"
            strokeWidth={1.6}
            strokeLinecap="round"
            aria-hidden="true"
            focusable="false"
          >
            <path d="M6 6l12 12M18 6L6 18" />
          </svg>
        </button>

        {children}
      </div>
    </div>,
    document.body,
  );
}
